import { t } from '@/i18n/i18n'
import { type SponsorsOrderChange } from '@/pages/sponsors/SponsorsOrderChange.type'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { type Sponsor, type SponsorCategory } from '@/shared/types/sponsor.type'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import { makeTranslatableText } from '../../../types/TranslatableText.type'
import router from '../../router'
import { IdsService } from '../../shared/service/utils.service'
import { InternalType } from '../../shared/types/files.type'
import { SponsorsService } from './sponsors.service'
import { SponsorsCategoriesService } from './sponsorsCategories.service'

function omitKey<T extends Record<string, unknown>>(record: T, key: string): T {
  const { [key]: _removed, ...rest } = record
  return rest as T
}

class SponsorsState {
  // Sponsors fields
  sponsors: Record<string, Sponsor> = {}
  loading = false
  deletingSponsorId: string | null = null
  editingSponsor: Sponsor | null = null
  image: string | null = null

  // Categories fields (merged from SponsorsCategoriesState)
  categoriesLoading = false
  sponsorsCategories: Record<string, SponsorCategory> = {}
  deletingSponsorCategoryId: string | null = null
  editingSponsorCategoryId: string | null = null
  creatingCategoryDefaultName: string | null = null
  showModal = false

  // comboBox (categories)
  selectedCategory: SponsorCategory | null = null
  categoriesSearch = ''
  addedCategories: Record<string, SponsorCategory> = {}
  deletedCategoriesIds: string[] = []
}

type SponsorsStore = SponsorsState & {
  // ── Sponsors mutations → actions ──
  load: () => void
  unload: () => void
  setSponsors: (sponsors: Sponsor[]) => void
  setDeleting: (id: string) => void
  setNotDeleting: () => void
  setImage: (image: string) => void
  reset: () => void
  setEditing: (sponsor: Sponsor) => void

  // ── Categories mutations → actions ──
  categoriesLoad: () => void
  categoriesUnload: () => void
  setNotDeletingCategory: () => void
  setSponsorsCategories: (sponsorsCategories: SponsorCategory[]) => void
  setDeletingCategory: (id: string) => void
  openAddSponsorCategory: () => void
  setEditingCategory: (id: string) => void
  resetEditingCategory: () => void
  openCreateCategoryModal: (name: string) => void
  resetCreatingCategory: () => void
  deleteCategory: (category: SponsorCategory) => void
  clearSelectedCategory: () => void
  setCategoriesSelection: (category: string | SponsorCategory | null) => void
  setCategoriesSearch: (categoriesSearch: string) => void

  // ── Sponsors actions ──
  list: () => Promise<void>
  delete: () => Promise<void>
  uploadImage: (file: File) => Promise<void>
  addSponsor: (sponsor: Sponsor) => Promise<void>
  loadSponsor: (id: string) => Promise<void>
  updateOrder: (payload: SponsorsOrderChange) => Promise<void>

  // ── Categories actions ──
  categoriesList: () => Promise<void>
  deleteSponsorCategory: () => Promise<void>
  addSponsorCategory: (sponsorCategory: SponsorCategory) => Promise<void>
  updateSponsorsCategories: () => Promise<void>
}

// --- Getter computation helpers ---------------------------------------------
function computeGetCategories(state: SponsorsState): SponsorCategory[] {
  return Object.values(state.sponsorsCategories)
}

export const useSponsorsStore = create<SponsorsStore>((set, get) => ({
  ...new SponsorsState(),

  // ── Sponsors mutations → actions ──
  load: () => {
    set({ loading: true })
  },

  unload: () => {
    set({ loading: false })
  },

  setSponsors: (sponsors) => {
    set({
      sponsors: sponsors.reduce<Record<string, Sponsor>>((acc, sponsor) => {
        acc[sponsor._id] = sponsor
        return acc
      }, {}),
    })
  },

  setDeleting: (id) => {
    set({ deletingSponsorId: id })
  },

  setNotDeleting: () => {
    set({ deletingSponsorId: null })
  },

  setImage: (image) => {
    set({ image })
  },

  reset: () => {
    set({
      editingSponsor: null,
      image: null,
      selectedCategory: null,
      deletedCategoriesIds: [],
      addedCategories: {},
      categoriesSearch: '',
    })
  },

  setEditing: (sponsor) => {
    if (sponsor.category) {
      get().setCategoriesSelection(sponsor.category.nameNormalized)
    }
    set({ editingSponsor: sponsor, image: sponsor.image })
  },

  // ── Categories mutations → actions ──
  categoriesLoad: () => {
    set({ categoriesLoading: true })
  },

  categoriesUnload: () => {
    set({ categoriesLoading: false })
  },

  setNotDeletingCategory: () => {
    set({ deletingSponsorCategoryId: null })
  },

  setSponsorsCategories: (sponsorsCategories) => {
    set({
      sponsorsCategories: sponsorsCategories.reduce<
        Record<string, SponsorCategory>
      >((acc, sponsorCategory) => {
        if (sponsorCategory._id == null) return acc
        acc[sponsorCategory._id] = sponsorCategory
        return acc
      }, {}),
    })
  },

  setDeletingCategory: (id) => {
    set({ deletingSponsorCategoryId: id })
  },

  openAddSponsorCategory: () => {
    set({ showModal: true })
  },

  setEditingCategory: (id) => {
    set({ editingSponsorCategoryId: id })
  },

  resetEditingCategory: () => {
    set({ editingSponsorCategoryId: null })
  },

  openCreateCategoryModal: (name) => {
    set({ creatingCategoryDefaultName: name, categoriesSearch: '' })
  },

  resetCreatingCategory: () => {
    set({ creatingCategoryDefaultName: null })
  },

  deleteCategory: (category) => {
    if (category._id == null) return
    const state = get()
    if (category._id in state.addedCategories) {
      const addedCategories = omitKey(state.addedCategories, category._id)
      set({ addedCategories })
    } else {
      const sponsorsCategories = omitKey(state.sponsorsCategories, category._id)
      set({
        deletedCategoriesIds: [...state.deletedCategoriesIds, category._id],
        sponsorsCategories,
      })
    }
  },

  clearSelectedCategory: () => {
    set({ selectedCategory: null })
  },

  setCategoriesSelection: (_category) => {
    if (_category == null) return

    let category: SponsorCategory =
      typeof _category === 'string'
        ? {
            names: makeTranslatableText(_category),
            nameNormalized: _category.toLowerCase(),
            _id: undefined,
            weight: 100,
          }
        : _category

    const state = get()
    const tmpCategories = Object.values(state.sponsorsCategories).filter(
      (n) => n.nameNormalized === category.nameNormalized,
    )
    if (tmpCategories.length > 0) {
      ;[category] = tmpCategories
    } else if (
      Object.values(state.sponsorsCategories).filter(
        (n) => n.nameNormalized === category.nameNormalized,
      ).length === 0 &&
      Object.values(state.addedCategories).filter(
        (n) => n.nameNormalized === category.nameNormalized,
      ).length === 0
    ) {
      category._id = IdsService.generateId()
      set({
        addedCategories: {
          ...state.addedCategories,
          [category._id]: category,
        },
      })
    }

    set({ selectedCategory: category, categoriesSearch: '' })
  },

  setCategoriesSearch: (categoriesSearch) => {
    set({ categoriesSearch })
  },

  // ── Sponsors actions ──
  list: async () => {
    get().load()
    try {
      const sponsors = await SponsorsService.getAll()
      get().setSponsors(sponsors)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  delete: async () => {
    get().load()
    try {
      const state = get()
      const id = state.deletingSponsorId ?? ''
      await SponsorsService.delete(id)
      SnackbarService.info(
        t('SPONSORS.SNACKBAR.DELETE', {
          string: state.sponsors[id].names.fr,
        }),
      )
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setNotDeleting()
    }
  },

  uploadImage: async (file) => {
    get().load()
    try {
      const savedImage = await FilesService.uploadImageFile(
        file,
        InternalType.SPONSOR,
      )
      get().setImage(savedImage.filename)
    } catch (e) {
      ErrorService.handleError(e)
      throw e
    } finally {
      get().unload()
    }
  },

  addSponsor: async (sponsor) => {
    get().load()
    try {
      await get().updateSponsorsCategories()
      const state = get()
      const selectedCategory = cloneDeep(state.selectedCategory)
      sponsor.image = state.image
      sponsor.category = selectedCategory
        ? cloneDeep(
            Object.values(state.sponsorsCategories).filter(
              (n: SponsorCategory) =>
                n.nameNormalized === selectedCategory.nameNormalized,
            )[0],
          )
        : selectedCategory
      let message
      if (state.editingSponsor) {
        await SponsorsService.updateSponsor(sponsor)
        message = t('SPONSORS.SNACKBAR.EDIT', {
          string: sponsor.names.fr,
        })
      } else {
        await SponsorsService.addSponsor(sponsor)
        message = t('SPONSORS.SNACKBAR.ADD', {
          string: sponsor.names.fr,
        })
      }
      SnackbarService.info(message.toString())
      get().reset()
      get().unload()
      await router.push('/sponsors')
    } catch (e) {
      ErrorService.handleError(e)
      get().unload()
    }
  },

  loadSponsor: async (id) => {
    get().load()
    try {
      const sponsor = await SponsorsService.getSponsorById(id)
      get().setEditing(sponsor)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  updateOrder: async (payload) => {
    get().load()
    try {
      const categories = computeGetCategories(get())
      const sorted = payload.categories.reduce<Record<string, string[]>>(
        (acc: Record<string, string[]>, cat: string) => {
          const category = categories.find((c) => c.nameNormalized === cat)
          const id = category ? category.nameNormalized : 'noCategory'
          acc[id] = payload.sponsorsIdsByCategories[cat].map((s) => s._id)
          return acc
        },
        {},
      )
      await SponsorsService.updateOrder(sorted)
      SnackbarService.info(t('SPONSORS.SNACKBAR.ORDER_CHANGE'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  // ── Categories actions ──
  categoriesList: async () => {
    get().categoriesLoad()
    try {
      const sponsorsCategories = await SponsorsCategoriesService.getAll()
      get().setSponsorsCategories(sponsorsCategories)
      get().categoriesUnload()
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  deleteSponsorCategory: async () => {
    get().categoriesLoad()
    try {
      const state = get()
      if (state.deletingSponsorCategoryId == null) {
        return
      }
      await SponsorsCategoriesService.deleteSponsorCategory(
        state.deletingSponsorCategoryId,
      )
      SnackbarService.info(
        t('SPONSORS_CATEGORIES.SNACK_BAR.DELETE', {
          string:
            state.sponsorsCategories[state.deletingSponsorCategoryId].names.fr,
        }),
      )
      get().setNotDeletingCategory()
      await get().categoriesList()
      await get().list()
      get().categoriesUnload()
    } catch (e) {
      ErrorService.handleError(e)
      get().categoriesUnload()
    }
  },

  addSponsorCategory: async (sponsorCategory) => {
    get().categoriesLoad()
    try {
      const state = get()
      let message = ''
      if (state.editingSponsorCategoryId != null) {
        await SponsorsCategoriesService.updateSponsorCategory(sponsorCategory)
        message = t('SPONSORS_CATEGORIES.SNACK_BAR.EDIT', {
          string: sponsorCategory.names.fr,
        })
      } else {
        await SponsorsCategoriesService.addSponsorCategory(sponsorCategory)
        message = t('SPONSORS_CATEGORIES.SNACK_BAR.ADD', {
          string: sponsorCategory.names.fr,
        })
      }
      SnackbarService.info(message)
      await get().categoriesList()
      await get().list()
      get().resetEditingCategory()
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  updateSponsorsCategories: async () => {
    get().categoriesLoad()
    try {
      const state = get()
      const values = cloneDeep(Object.values(state.addedCategories))
      await Promise.all(
        values.map(async (item) => {
          delete item._id
          return await SponsorsCategoriesService.addSponsorCategory(item)
        }),
      )

      await Promise.all(
        state.deletedCategoriesIds.map(async (id) => {
          return SponsorsCategoriesService.deleteSponsorCategory(id)
        }),
      )
      await get().categoriesList()
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
}))
